import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router";
import { Button, Alert, Card, Spin } from "antd";
import { ExclamationCircleOutlined, HomeOutlined, LinkOutlined } from "@ant-design/icons";
import useRequest from "@/utils/useRequest";
import { useCommonStore } from "@/store/useCommonStore";

const BindPage = () => {
    const { request } = useRequest();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const user = useCommonStore(state => state.user);
    
    // 添加状态管理
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [success, setSuccess] = useState(false);

    const bindRequest = async (code, state) => {
        try {
            setLoading(true);
            const result = await request("/api/v2/public/auth/bind", "POST", "json", { code, state });
            console.log("bind 结果", result);
            
            // 判断返回结果是否成功
            if (result.success) {
                // 绑定成功
                setSuccess(true);
                setLoading(false);
                
                // 可以选择更新用户信息到store
                if (result.data && result.data.user) {
                    useCommonStore.setState(state => {
                        state.user = {
                            ...state.user,
                            ...result.data.user,
                        };
                    });
                }
                
                // 延迟跳转到个人资料页面
                setTimeout(() => {
                    navigate("/profile/settings");
                }, 2000);
            } else {
                console.error("绑定失败：", result.message);
                // 设置错误状态，显示错误信息
                setError(true);
                setErrorMessage(result.message || "绑定失败，请重试");
                setLoading(false);
            }
        } catch (err) {
            console.error("绑定请求失败：", err);
            // 处理请求异常，显示错误信息
            setError(true);
            setErrorMessage("网络请求失败，请检查网络连接后重试");
            setLoading(false);
        }
    };

    const handleBackToProfile = () => {
        navigate("/profile/settings");
    };

    const handleBackToLogin = () => {
        navigate("/login");
    };

    useEffect(() => {
        // 检查用户是否已登录
        if (!user) {
            console.error("用户未登录，无法进行绑定操作");
            setError(true);
            setErrorMessage("请先登录后再进行账户绑定");
            setLoading(false);
            return;
        }

        const storedState = sessionStorage.getItem("auth_state"); // 获取缓存的 state
        const receivedState = searchParams.get("state"); // 从 URL 参数中获取 state
        const code = searchParams.get("code"); // 获取授权码

        // 验证 state
        if (!storedState || storedState !== receivedState) {
            console.error("State 验证失败！");
            // 处理验证失败逻辑，显示错误信息
            setError(true);
            setErrorMessage("安全验证失败，请重新尝试绑定");
            setLoading(false);
            return;
        }

        console.log("State 验证通过，授权码为:", code);
        // 使用授权码请求绑定
        bindRequest(code, receivedState);
    }, [searchParams, user]);

    // 加载中界面
    if (loading && !error && !success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Card className="w-96 text-center shadow-lg">
                    <Spin size="large" />
                    <div className="mt-4 text-lg text-gray-600">正在处理账户绑定...</div>
                    <div className="mt-2 text-sm text-gray-400">请稍候，系统正在绑定您的第三方账户</div>
                </Card>
            </div>
        );
    }

    // 成功界面
    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Card className="w-96 shadow-lg">
                    <div className="text-center">
                        <LinkOutlined className="text-6xl text-green-500 mb-4" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-4">绑定成功</h2>
                        
                        <Alert
                            message="绑定完成"
                            description="您的第三方账户已成功绑定，即将跳转到个人资料页面"
                            type="success"
                            showIcon
                            className="mb-6 text-left"
                        />
                        
                        <div className="space-y-3">
                            <Button 
                                type="primary" 
                                size="large" 
                                icon={<HomeOutlined />}
                                onClick={handleBackToProfile}
                                className="w-full"
                            >
                                前往个人资料
                            </Button>
                        </div>
                        
                        <div className="mt-6 text-xs text-gray-400">
                            页面将在2秒后自动跳转
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    // 错误界面
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Card className="w-96 shadow-lg">
                    <div className="text-center">
                        <ExclamationCircleOutlined className="text-6xl text-red-500 mb-4" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-4">绑定失败</h2>
                        
                        <Alert
                            message="错误信息"
                            description={errorMessage}
                            type="error"
                            showIcon
                            className="mb-6 text-left"
                        />
                        
                        <div className="space-y-3">
                            {user ? (
                                <Button 
                                    type="primary" 
                                    size="large" 
                                    icon={<HomeOutlined />}
                                    onClick={handleBackToProfile}
                                    className="w-full"
                                >
                                    返回个人资料
                                </Button>
                            ) : (
                                <Button 
                                    type="primary" 
                                    size="large" 
                                    icon={<HomeOutlined />}
                                    onClick={handleBackToLogin}
                                    className="w-full"
                                >
                                    返回登录页面
                                </Button>
                            )}
                            
                            <Button 
                                size="large" 
                                onClick={() => window.location.reload()}
                                className="w-full"
                            >
                                重新尝试
                            </Button>
                        </div>
                        
                        <div className="mt-6 text-xs text-gray-400">
                            如果问题持续存在，请联系系统管理员
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    return null;
};

export default BindPage;